/**
 * Notification Service
 * Handles notification operations with Firebase
 */

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc,
  addDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Get user notifications
 * 
 * @param {string} userId - The user ID
 * @param {number} limitCount - Number of notifications to retrieve
 * @returns {Promise<Array>} Array of notification objects
 */
export const getUserNotifications = async (userId, limitCount = 20) => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date()
    }));
  } catch (error) {
    console.error('Error getting user notifications:', error);
    throw error;
  }
};

/**
 * Subscribe to user notifications
 * 
 * @param {string} userId - The user ID
 * @param {function} callback - Callback function to handle notifications
 * @returns {function} Unsubscribe function
 */
export const subscribeToNotifications = (userId, callback) => {
  const notificationsQuery = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(20)
  );
  
  return onSnapshot(notificationsQuery, (snapshot) => {
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date()
    }));
    
    callback(notifications);
  }, (error) => {
    console.error('Error subscribing to notifications:', error);
  });
};

/**
 * Subscribe to connection-related notifications only
 * 
 * @param {string} userId - The user ID
 * @param {function} callback - Callback function to handle connection notifications
 * @returns {function} Unsubscribe function
 */
export const subscribeToConnectionNotifications = (userId, callback) => {
  const notificationsQuery = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    where('type', 'in', ['connection_request', 'connection_accepted']),
    orderBy('createdAt', 'desc'),
    limit(20)
  );
  
  return onSnapshot(notificationsQuery, (snapshot) => {
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date()
    }));
    
    callback(notifications);
  }, (error) => {
    console.error('Error subscribing to connection notifications:', error);
  });
};

/**
 * Get unread connection notification count
 * 
 * @param {string} userId - The user ID
 * @returns {Promise<number>} Number of unread connection notifications
 */
export const getUnreadConnectionNotificationCount = async (userId) => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('type', 'in', ['connection_request', 'connection_accepted']),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting unread connection notification count:', error);
    throw error;
  }
};

/**
 * Get unread general notification count (excluding connection notifications)
 * 
 * @param {string} userId - The user ID
 * @returns {Promise<number>} Number of unread general notifications
 */
export const getUnreadGeneralNotificationCount = async (userId) => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    
    // Filter out connection-related notifications
    const generalNotifications = snapshot.docs.filter(doc => {
      const type = doc.data().type;
      return type !== 'connection_request' && type !== 'connection_accepted';
    });
    
    return generalNotifications.length;
  } catch (error) {
    console.error('Error getting unread general notification count:', error);
    throw error;
  }
};

/**
 * Mark notification as read
 * 
 * @param {string} notificationId - The notification ID
 * @returns {Promise<void>}
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      isRead: true
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * 
 * @param {string} userId - The user ID
 * @returns {Promise<void>}
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    
    const updatePromises = snapshot.docs.map(doc => 
      updateDoc(doc.ref, { isRead: true })
    );
    
    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Mark all connection notifications as read
 * 
 * @param {string} userId - The user ID
 * @returns {Promise<void>}
 */
export const markAllConnectionNotificationsAsRead = async (userId) => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('type', 'in', ['connection_request', 'connection_accepted']),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    
    const updatePromises = snapshot.docs.map(doc => 
      updateDoc(doc.ref, { isRead: true })
    );
    
    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking all connection notifications as read:', error);
    throw error;
  }
};

/**
 * Create a new notification
 * 
 * @param {Object} notification - The notification object
 * @returns {Promise<string>} The notification ID
 */
export const createNotification = async (notification) => {
  try {
    const notificationData = {
      ...notification,
      isRead: false,
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'notifications'), notificationData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Create a message notification
 * 
 * @param {string} recipientId - Recipient user ID
 * @param {string} senderId - Sender user ID
 * @param {string} senderName - Sender name
 * @param {string} senderPhoto - Sender photo URL
 * @param {string} message - Message content preview
 * @returns {Promise<string>} - Notification ID
 */
export const createMessageNotification = async (recipientId, senderId, senderName, senderPhoto, message) => {
  try {
    return await createNotification({
      userId: recipientId,
      type: 'message',
      content: {
        senderId,
        senderName,
        senderPhoto,
        message: message.length > 100 ? message.substring(0, 100) + '...' : message
      }
    });
  } catch (error) {
    console.error('Error creating message notification:', error);
    throw error;
  }
};

/**
 * Create a resource review notification
 * 
 * @param {string} userId - User ID who submitted the resource
 * @param {string} resourceTitle - Title of the resource
 * @param {string} status - 'active' (approved) or 'rejected'
 * @param {string} reason - Reason for rejection (optional)
 * @returns {Promise<string>} - Notification ID
 */
export const createResourceNotification = async (userId, resourceTitle, status, reason = '') => {
  try {
    let content;
    
    if (status === 'active') {
      content = {
        title: '🎉 Resource Approved!',
        message: `Your resource "${resourceTitle}" has been approved and is now live! Thanks for contributing to the VarsityLink community.`,
        resourceTitle,
        status: 'approved',
        action: 'approved'
      };
    } else if (status === 'rejected') {
      content = {
        title: '📝 Resource Review',
        message: `Your resource "${resourceTitle}" needs some adjustments before it can be published.${reason ? ` Reason: ${reason}` : ''}`,
        resourceTitle,
        status: 'rejected',
        reason,
        action: 'rejected'
      };
    }

    return await createNotification({
      userId,
      type: 'resource_review',
      content
    });
  } catch (error) {
    console.error('Error creating resource notification:', error);
    throw error;
  }
};

/**
 * Get unread notification count (all notifications)
 * 
 * @param {string} userId - The user ID
 * @returns {Promise<number>} Number of unread notifications
 */
export const getUnreadNotificationCount = async (userId) => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting unread notification count:', error);
    throw error;
  }
};

/**
 * Format notification time
 * 
 * @param {Date} date - The date to format
 * @returns {string} Formatted time string
 */
export const formatNotificationTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const diff = Math.floor((now - date) / 1000); // diff in seconds
  
  if (diff < 60) {
    return 'just now';
  } else if (diff < 3600) {
    const minutes = Math.floor(diff / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diff < 604800) {
    const days = Math.floor(diff / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  }
};

/**
 * Mark all notifications from a specific user with a specific type as read
 * 
 * @param {string} userId - Recipient user ID
 * @param {string} fromUserId - Sender user ID 
 * @param {string} type - Notification type
 * @returns {Promise<void>}
 */
export const markNotificationsFromUserAsRead = async (userId, fromUserId, type) => {
  try {
    // Query notifications that match our criteria
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('type', '==', type),
      where('isRead', '==', false)
    );
    
    const snapshot = await getDocs(notificationsQuery);
    
    // Filter for notifications from the specific user
    const updatePromises = snapshot.docs
      .filter(doc => doc.data().content?.senderId === fromUserId)
      .map(doc => updateDoc(doc.ref, { isRead: true }));
    
    if (updatePromises.length > 0) {
      await Promise.all(updatePromises);
    }
  } catch (error) {
    console.error(`Error marking notifications from user ${fromUserId} as read:`, error);
    throw error;
  }
};

export default {
  getUserNotifications,
  subscribeToNotifications,
  subscribeToConnectionNotifications, // NEW: Connection-specific subscription
  markNotificationAsRead,
  markAllNotificationsAsRead,
  markAllConnectionNotificationsAsRead, // NEW: Mark connection notifications as read
  createNotification,
  createMessageNotification,
  createResourceNotification,
  markNotificationsFromUserAsRead,
  getUnreadNotificationCount,
  getUnreadConnectionNotificationCount, // NEW: Connection notification count
  getUnreadGeneralNotificationCount, // NEW: General notification count (excluding connections)
  formatNotificationTime
};